import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, Users } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtEuro, parseNum } from "@/lib/dates";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} (new) | job
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  const [assigned, setAssigned] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [j, e] = await Promise.all([api.get("/jobs"), api.get("/users")]);
    setJobs(j.data);
    setEmployees(e.data);
  };

  useEffect(() => {
    load().catch(() => toast.error("Errore nel caricamento"));
  }, []);

  const openNew = () => {
    setEditing({});
    setName("");
    setRate("");
    setAssigned([]);
  };
  const openEdit = (job) => {
    setEditing(job);
    setName(job.name);
    setRate(job.hourly_rate ? String(job.hourly_rate).replace(".", ",") : "");
    setAssigned(job.assigned_employee_ids || []);
  };

  const toggle = (id) => {
    setAssigned((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Inserisci il nome dell'azienda");
    setBusy(true);
    try {
      const payload = { name, assigned_employee_ids: assigned, hourly_rate: parseNum(rate) };
      if (editing.id) {
        await api.put(`/jobs/${editing.id}`, payload);
        toast.success("Lavoro aggiornato");
      } else {
        await api.post("/jobs", payload);
        toast.success("Lavoro creato");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/jobs/${deleteTarget.id}`);
      toast.success("Lavoro eliminato (lo storico resta disponibile)");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";
  const inputCls = "w-full h-14 rounded-xl border border-slate-200 px-4 text-lg outline-none focus:border-primary";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-head text-2xl font-bold text-slate-900">Lavori</h1>
        <button data-testid="new-job-button" onClick={openNew} className="h-12 px-4 rounded-xl bg-primary text-primary-foreground font-bold flex items-center gap-1.5 active:scale-95">
          <Plus className="h-5 w-5" /> Nuovo
        </button>
      </div>

      <div className="space-y-2">
        {jobs.length === 0 && <p className="text-slate-400 text-sm">Nessun lavoro attivo. Crea il primo.</p>}
        {jobs.map((j) => (
          <div key={j.id} data-testid="job-card" className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 text-lg">{j.name}</p>
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                  <Users className="h-4 w-4" />
                  {(j.assigned_employee_ids || []).length === 0
                    ? "Nessun dipendente"
                    : j.assigned_employee_ids.map(empName).join(", ")}
                </p>
                <p className="text-sm mt-0.5">
                  {j.hourly_rate ? (
                    <span data-testid={`job-rate-${j.id}`} className="font-semibold text-primary">{fmtEuro(j.hourly_rate)}/h netti</span>
                  ) : (
                    <span className="text-amber-500">Prezzo non impostato</span>
                  )}
                </p>
              </div>
              <div className="flex gap-2 ml-2">
                <button data-testid={`edit-job-${j.id}`} onClick={() => openEdit(j)} className="h-10 w-10 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 active:scale-95">
                  <Pencil className="h-4 w-4" />
                </button>
                <button data-testid={`delete-job-${j.id}`} onClick={() => setDeleteTarget(j)} className="h-10 w-10 rounded-lg border border-red-200 flex items-center justify-center text-destructive active:scale-95">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head">{editing?.id ? "Modifica lavoro" : "Nuovo lavoro"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <input data-testid="job-name-input" className={inputCls} placeholder="Nome azienda / cantiere" value={name} onChange={(e) => setName(e.target.value)} />
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-1 block">Prezzo orario netto (senza IVA)</label>
              <input data-testid="job-rate-input" inputMode="decimal" className={inputCls} placeholder="es. 25,00 €/h" value={rate} onChange={(e) => setRate(e.target.value)} />
              <p className="text-xs text-slate-400 mt-1">Quanto fatturi al cliente per ogni ora lavorata su questo lavoro.</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-600 mb-2">Dipendenti assegnati</p>
              <div className="space-y-2">
                {employees.length === 0 && <p className="text-sm text-slate-400">Crea prima dei dipendenti.</p>}
                {employees.map((e) => {
                  const on = assigned.includes(e.id);
                  return (
                    <button
                      key={e.id}
                      data-testid={`assign-${e.username}`}
                      onClick={() => toggle(e.id)}
                      className={`w-full h-12 rounded-xl border px-4 flex items-center justify-between text-left font-semibold active:scale-[0.99] transition-colors ${
                        on ? "border-primary bg-accent text-accent-foreground" : "border-slate-200 text-slate-700"
                      }`}
                    >
                      {e.name}
                      <span className={`h-5 w-5 rounded-md border flex items-center justify-center ${on ? "bg-primary border-primary" : "border-slate-300"}`}>
                        {on && <span className="text-white text-xs">✓</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="save-job-button" onClick={save} disabled={busy} className="w-full h-14 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center active:scale-[0.98] disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Salva"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Il lavoro sparirà dalle liste attive, ma le ore già registrate resteranno nello storico e nei report.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-job" onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
